import JigsawPuzzle from "./jigsaw/JigsawPuzzle";
import WordSearch from "./wordsearch/WordSearch";
import Trivia from "./trivia/Trivia";
import WhackAMole from "./whackamole/WhackAMole";
import Crossword from "./crossword/Crossword";
import EmbeddedGame from "./EmbeddedGame";

/**
 * Game type → player. A future native game adds one entry here (and its
 * backend GameType in App\Services\Games\GameTypeRegistry).
 *
 * hasLevels: the game's playable content comes from game.levels and the
 * player renders one level at a time. shareable: the native engine reports a
 * real completion (embedded third-party games have no trustworthy signal, so
 * they never offer Share to Community). serverSession: the player needs the
 * signed play session to play at all (Trivia and Crossword answers are checked
 * on the server; Whack-a-Mole gets its round schedule from the session start), so it
 * receives playToken / playError / playData (the start response).
 */
export const GAME_TYPES = {
  jigsaw: {
    label: "Jigsaw Puzzle",
    hasLevels: true,
    shareable: true,
    render: (game, level) => ({
      Component: JigsawPuzzle,
      props: {
        imageUrl: level.image_url,
        imageWidth: level.image_width,
        imageHeight: level.image_height,
        pieces: level.config && level.config.pieces,
        defaultDifficulty: level.config && level.config.default_difficulty,
        title: game.title + " — " + level.name,
      },
    }),
  },
  wordsearch: {
    label: "Word Search",
    hasLevels: true,
    shareable: true,
    render: (game, level) => ({
      Component: WordSearch,
      props: {
        grid: level.config && level.config.grid,
        words: level.config && level.config.words,
        directions: level.config && level.config.directions,
        difficulty: level.config && level.config.difficulty,
        title: game.title + " — " + level.name,
      },
    }),
  },
  trivia: {
    label: "Trivia",
    hasLevels: true,
    shareable: true,
    serverSession: true,
    render: (game, level) => ({
      Component: Trivia,
      props: {
        questions: level.config && level.config.questions,
        gameTitle: game.title,
        levelName: level.name,
      },
    }),
  },
  whackamole: {
    label: "Whack-a-Mole",
    hasLevels: true,
    shareable: true,
    serverSession: true,
    render: (game, level) => ({
      Component: WhackAMole,
      props: {
        config: level.config,
        assets: level.assets,
        gameTitle: game.title,
        levelName: level.name,
      },
    }),
  },
  crossword: {
    label: "Crossword",
    hasLevels: true,
    shareable: true,
    serverSession: true,
    render: (game, level) => ({
      Component: Crossword,
      props: {
        layout: level.config,
        gameTitle: game.title,
        levelName: level.name,
      },
    }),
  },
  embed: {
    label: "Embedded Game",
    hasLevels: false,
    shareable: false,
    render: (game) => ({
      Component: EmbeddedGame,
      props: {
        embedUrl: game.config && game.config.embed_url,
        aspectRatio: game.config && game.config.aspect_ratio,
        title: game.title,
      },
    }),
  },
};

export function gameTypeFor(type) {
  return Object.prototype.hasOwnProperty.call(GAME_TYPES, type) ? GAME_TYPES[type] : null;
}

/**
 * Level chosen by ?level=<id> (only available levels are ever listed by the
 * API). A single-level game opens its level directly.
 * @returns {{ level: object|null, invalid: boolean }}
 */
export function selectLevel(levels, search) {
  const list = Array.isArray(levels) ? levels : [];
  const match = /[?&]level=([^&#]*)/.exec(search || "");
  const requested = match ? decodeURIComponent(match[1]) : "";
  if (requested) {
    for (let i = 0; i < list.length; i++) {
      if (String(list[i].id) === requested) return { level: list[i], invalid: false };
    }
    return { level: null, invalid: true };
  }
  return { level: list.length === 1 ? list[0] : null, invalid: false };
}

/** Link that opens one level of a game. */
export function levelPath(slug, levelId) {
  return "/games/" + encodeURIComponent(slug) + "?level=" + encodeURIComponent(levelId);
}
